import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Logo } from "./Logo";
import { useAuth } from "../../context/AuthContext";
import { NavigationLinks } from "./NavigationLinks";
import { UserMenu } from "./UserMenu";
import { MobileMenu } from "./MobileMenu";
import { HamburgerButton } from "./HamburgerButton";
import { ThemeToggler } from "../common/ThemeToggler";
import { SearchPalette } from "../common/SearchPalette";
import { Icon } from "../common/Icon";
import { FontSizeControl } from "../common/FontSizeControl";
import { LocaleSwitcher } from "../common/LocaleSwitcher";
import { useLocale } from "../../context/LocaleContext";

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const { availableLocales } = useLocale();

  // Hide auth-related items on login/register pages
  const isAuthPage = location.pathname === '/login' || location.pathname === '/register';
  const shouldShowAuth = isAuthenticated && !isAuthPage;
  // Font size control is only useful while reading a blog post
  const isPostPage = /^\/blog\/[^/]+\/[^/]+/.test(location.pathname);
  // Where to send the reader back to once they have signed in
  const currentPath = `${location.pathname}${location.search}`;

  const [isVisible, setIsVisible] = useState(true);
  const lastScrollYRef = useRef<number>(0);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const controlNavbar = () => {
      const currentScrollY = window.scrollY;
      setHasScrolled(currentScrollY > 0);
      const last = lastScrollYRef.current;
      if (currentScrollY < last || currentScrollY < 100 || isMobileMenuOpen) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
      lastScrollYRef.current = currentScrollY;
    };

    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => { controlNavbar(); ticking = false; });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [isMobileMenuOpen]);

  // Dismissing the user menu lives in UserMenu itself. It used to be here,
  // guarded on a ref that was never attached to anything — so the guard was
  // always null and the menu never closed on an outside click.

  // Escape closes the mobile menu; Cmd/Ctrl-K toggles the search palette
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
      if (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setIsSearchOpen(open => !open);
        setIsMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  /**
   * Publish how much room the header is actually taking, as --header-offset.
   *
   * Anything else that sticks to the top has to follow this or it leaves a
   * gap: the header hides itself on a downward scroll, and it is 72px tall at
   * rest but 56px once scrolled. A hard-coded `top-16` is wrong in both
   * states — too small at rest, and a 64px band of nothing when the header
   * has slid away.
   */
  useEffect(() => {
    const element = headerRef.current;
    if (!element) return;

    const publish = () => {
      const height = isVisible ? element.getBoundingClientRect().height : 0;
      document.documentElement.style.setProperty('--header-offset', `${Math.round(height)}px`);
    };

    publish();

    // The header changes height when the page scrolls (py-4 to py-2).
    const observer = new ResizeObserver(publish);
    observer.observe(element);

    return () => observer.disconnect();
  }, [isVisible]);

  const closeMobileMenu = () => setIsMobileMenuOpen(false);

  const handleLogout = async () => {
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
    await logout();
    navigate('/');
  };

  const handleNavClick = () => {
    closeMobileMenu();
    window.scrollTo({ top: 0 });
  };

  return (
    <header
      ref={headerRef}
      className={`sticky top-0 z-50 w-full transition-all duration-300 transform ${isVisible ? 'translate-y-0' : '-translate-y-full'}`}
    >
      <div className={`w-full bg-background/70 backdrop-blur-xl shadow-[0_1px_0_0_rgba(var(--shadow-separator,0,0,0),0.08)] transition-all duration-300 ${hasScrolled ? 'py-2' : 'py-4'}`}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <nav className="flex items-center justify-between">
            <Logo />

            {/* Desktop nav links */}
            <NavigationLinks className="hidden md:flex items-center gap-6" onNavClick={handleNavClick} />

            {/* Desktop auth section */}
            <div className="hidden md:flex items-center gap-3">
              {/* Search — looks like a field, behaves like a button (⌘K) */}
              <button
                type="button"
                onClick={() => setIsSearchOpen(true)}
                aria-label="Search (Command K)"
                className="hidden lg:flex items-center gap-2 h-9 w-[240px] px-3 rounded-full border border-border bg-background text-muted-foreground hover:border-primary/40 hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <Icon name="search" size={16} />
                <span className="text-sm flex-1 text-left">Search</span>
                <kbd className="text-[11px] font-semibold border border-border rounded-md px-1.5 py-0.5">⌘K</kbd>
              </button>
              <button
                type="button"
                onClick={() => setIsSearchOpen(true)}
                aria-label="Search"
                className="lg:hidden grid place-items-center h-9 w-9 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                <Icon name="search" size={18} />
              </button>
              {isPostPage && (
                <>
                  <LocaleSwitcher availableLocales={availableLocales} size="md" />
                  <FontSizeControl size="md" />
                  <span className="h-5 w-px bg-border opacity-40" aria-hidden="true" />
                </>
              )}
              <ThemeToggler />
              {/* Primary CTA — one per page, repeated in the mobile menu */}
              {!isAuthPage && !isAuthenticated && (
                <Link
                  to="/register"
                  state={{ from: currentPath }}
                  onClick={handleNavClick}
                  className="bg-primary text-primary-foreground px-5 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 hover:bg-primary-container active:scale-95 shadow-sm"
                >
                  Join free
                </Link>
              )}
              {shouldShowAuth ? (
                <UserMenu
                  user={user!}
                  isAdmin={isAdmin}
                  isOpen={isUserMenuOpen}
                  onToggle={() => setIsUserMenuOpen(v => !v)}
                  onLogout={handleLogout}
                  onClose={() => setIsUserMenuOpen(false)}
                />
              ) : !isAuthPage ? (
                <Link
                  to="/login"
                  state={{ from: currentPath }}
                  onClick={handleNavClick}
                  className="inline-flex items-center justify-center rounded-lg border border-border bg-background hover:bg-muted hover:text-foreground focus:ring-2 focus:ring-offset-2 focus:ring-primary font-medium transition-colors h-8 px-3 text-sm"
                >
                  Sign in
                </Link>
              ) : null}
            </div>

            {/* Mobile: search + hamburger, kept together at the end */}
            <div className="flex md:hidden items-center gap-0.5 ml-auto">
              <button
                type="button"
                onClick={() => setIsSearchOpen(true)}
                aria-label="Search"
                className="grid place-items-center h-11 w-11 rounded-xl text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <Icon name="search" size={22} />
              </button>
              <HamburgerButton ref={hamburgerRef} isOpen={isMobileMenuOpen} onClick={() => setIsMobileMenuOpen(v => !v)} />
            </div>
          </nav>

          {/* Mobile menu */}
          <MobileMenu
            isOpen={isMobileMenuOpen}
            user={user}
            isAuthenticated={isAuthenticated}
            isAdmin={isAdmin}
            onNavClick={handleNavClick}
            onLogout={handleLogout}
            onClose={closeMobileMenu}
            isAuthPage={isAuthPage}
            returnTo={currentPath}
            onOpenSearch={() => { setIsMobileMenuOpen(false); setIsSearchOpen(true); }}
          />
        </div>
      </div>

      <SearchPalette open={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </header>
  );
}